import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Share2, Star } from "lucide-react";
import { api } from "../lib/api";
import { useCatalog } from "../lib/useCatalog";
import { useFavorites } from "../context/FavoritesContext";
import { ModelCard } from "../components/ModelCard";
import { decodeFavorites } from "../components/ShareFavorites";
import { TrendingFavorite } from "./Favorites";
import { Button } from "../components/ui/button";

function useSharedList() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [state, setState] = useState({ loading: true });
  useEffect(() => {
    if (token) {
      api.get(`/shared/${token}`)
        .then(({ data }) => setState({ items: data.items, owner: data.owner }))
        .catch(() => setState({ error: "Ce lien de partage est invalide ou a été désactivé." }));
      return;
    }
    try {
      setState({ items: decodeFavorites(params.get("d") || "") });
    } catch {
      setState({ error: "Ce lien de partage est illisible." });
    }
  }, [token, params]);
  return state;
}

export default function SharedFavorites() {
  const { items, owner, error, loading } = useSharedList();
  const { data } = useCatalog();
  const { addMany, loaded } = useFavorites();
  const catalog = (items || []).filter((i) => i.kind === "catalog").map((i) => data?.models.find((m) => m.id === i.ref)).filter(Boolean);
  const trending = (items || []).filter((i) => i.kind === "trending");

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 md:px-8" data-testid="shared-favorites-page">
      <span className="flex items-center gap-1.5 font-mono2 text-xs uppercase tracking-widest text-amber-400"><Share2 className="h-4 w-4" /> Liste partagée</span>
      <h1 className="mt-2 font-head text-3xl font-extrabold tracking-tight">{owner ? `Les modèles favoris de ${owner}` : "Modèles favoris partagés"}</h1>
      {loading ? <p className="mt-6 text-muted-foreground">Chargement…</p> : error ? (
        <p data-testid="shared-error" className="mt-6 rounded-xl border border-red-900 bg-red-950/30 p-4 text-sm text-red-400">{error}</p>
      ) : (
        <>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button onClick={() => addMany(items)} disabled={!items.length || !loaded} data-testid="shared-add-all-button" className="gap-1.5"><Star className="h-4 w-4" /> Ajouter tout à mes favoris</Button>
            <span className="text-sm text-muted-foreground" data-testid="shared-count">{items.length} modèle(s) · compatibilité calculée pour <Link to="/" className="text-primary hover:underline">votre matériel</Link></span>
          </div>
          <div className="mt-8 space-y-8">
            {catalog.length > 0 && (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {catalog.map((m) => <ModelCard key={m.id} model={m} quantizations={data.quantizations} />)}
              </div>
            )}
            {trending.length > 0 && (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {trending.map((i) => <TrendingFavorite key={i.ref} item={i} />)}
              </div>
            )}
            {!items.length && <p data-testid="shared-empty" className="text-sm text-muted-foreground">Cette liste est vide pour le moment.</p>}
          </div>
        </>
      )}
    </div>
  );
}
